/* import __COLOCATED_TEMPLATE__ from './admin-companies.hbs'; */
import { task } from 'ember-concurrency';
import { not } from '@ember/object/computed';
import { computed } from '@ember/object';
import { A } from '@ember/array';
import Component from '@ember/component';
import { inject as service } from '@ember/service';
import { COMPANY } from '../../models/company';

const CLASS_NAME = 'SH-c-admin-companies';

const NULL_OPTION_LABEL = 'Any';

const INVOICE_OPTION_LABEL = 'Invoice';
const CARD_OPTION_LABEL = 'Credit Card';

const PAYMENT_METHOD_LABELS = {
  0: CARD_OPTION_LABEL,
  1: INVOICE_OPTION_LABEL,
};

const PAYMENT_METHOD_OPTIONS = [
  {
    key: NULL_OPTION_LABEL,
    model: null,
  },
  {
    key: PAYMENT_METHOD_LABELS[1],
    model: 1,
  },
  {
    key: PAYMENT_METHOD_LABELS[0],
    model: 0,
  },
];

const COMPANY_TIER_OPTIONS = [
  {
    key: NULL_OPTION_LABEL,
    model: { id: null, label: NULL_OPTION_LABEL },
  },
  ...Object.keys(COMPANY.TIERS).map((key) => ({
    key: COMPANY.TIER_NAMES[COMPANY.TIERS[key]],
    model: { id: COMPANY.TIERS[key], label: COMPANY.TIER_NAMES[COMPANY.TIERS[key]] },
  })),
];

export default Component.extend({
  companyGroupsCommandService: service('commands/company-group-commands'),
  shData: service(),

  tagName: '',
  CLASS_NAME,
  COMPANY_TIER_OPTIONS,
  PAYMENT_METHOD_OPTIONS,

  /** @param {Ember.A<Company>} */
  companies: A([]),

  /** @param {String|null} */
  idOrSlug: null,

  /** @param {String|null} */
  name: null,

  /** @param {Number|null} */
  tier: null,

  /** @param {Number|null} */
  companyGroupId: null,

  /** @param {Number|null} */
  paymentMethod: null,

  changeUser: null,

  user: null,

  /** @param {Boolean} */
  hasData: false,

  /** @param {Pagination|null} */
  pagination: null,

  /** @param */
  onUserCompanyChange: null,

  /** @param {Function} */
  onSearch: () => {},

  /** @param {Function} */
  onReset: () => {},

  /** @param {Boolean} */
  isLoading: false,

  /** @param {Boolean} */
  isUserLoading: false,

  /** @param {Function} */
  onEdit: () => {},

  /** @param {Function} */
  onPreviousPage: () => {},

  /** @param {Function} */
  onNextPage: () => {},

  /** @param {Function} */
  onProfileLinkClick: () => {},

  /** @param {Function} */
  onProfileOverlayClose: () => {},

  isNotBuyer: computed('user.isBuyer', 'changeUser', function(){
    return Boolean(this.changeUser && !this.user.isBuyer);
  }),

  hasPostings: computed('user.hasAssignments', 'changeUser', function(){
    return Boolean(this.changeUser && this.user.hasAssignments);
  }),

  /** @type {String} */
  tierLabel: computed('tier', function () {
    return COMPANY.TIER_NAMES[this.tier] || NULL_OPTION_LABEL;
  }),

  paymentMethodLabel: computed('paymentMethod', function () {
    return PAYMENT_METHOD_LABELS[this.paymentMethod] || NULL_OPTION_LABEL;
  }),

  companyGroupLabel: computed(
    '_availableCompanyGroups.@each.{id,name}',
    'companyGroupId',
    function() {
      // Show the selected company name if any
      const company = this._availableCompanyGroups.findBy('id', this.companyGroupId);

      return company?.name ?? NULL_OPTION_LABEL;
    }
  ),

  /** @type {A<Object>} */
  companyGroupOptions: computed(
    '_availableCompanyGroups.@each.{id,name}',
    function () {
      const availableCompanyGroups = this._availableCompanyGroups ?? [];

      return [
        {
          key: NULL_OPTION_LABEL,
          model: null,
        },
        ...availableCompanyGroups.map((companyGroup) => ({
          key: companyGroup.name,
          model: companyGroup.id,
        })),
      ];
    }
  ),

  /** @type {Boolean} */
  isResetDisabled: not('hasLocalData'),

  /** @type {Boolean} */
  hasLocalData: computed('idOrSlug', 'name', 'tier', 'paymentMethod', function () {
    return this.idOrSlug !== null
      || this.name !== null
      || this.tier !== null
      || this.paymentMethod !== null;
  }),

  /** @type {Boolean} */
  noResults: computed('hasData', 'companies.[]', function () {
    return this.hasData && !this.companies?.length;
  }),

  /** @type {Boolean} */
  showPagination: computed('isLoading', 'companies.[]', function () {
    return !this.isLoading && this.companies?.length;
  }),

  /** @inheritDoc */
  willDestroyElement() {
    this.onProfileOverlayClose();
  },

  actions: {
    /**
     * @param {String} field
     * @param {String|null} value
     */
    handleInput(field, value) {
      this.set(field, value);
    },

    /**
     * @param {String} field
     * @param {Object} selection
     */
    handleTierSelect(selection) {
      this.set('tier', selection.id ?? null);
    },

    /**
     * @param {String} field
     * @param {String|null} value
     */
     handleSelect(field, value) {
      this.set(field, value);
    },

    handleSearch() {
      const { idOrSlug, name, tier, companyGroupId, paymentMethod } = this.getProperties(['idOrSlug', 'name', 'tier', 'companyGroupId', 'paymentMethod']);

      this.onSearch({
        idOrSlug: idOrSlug || null,
        name: name || null,
        tier,
        groupId: companyGroupId || null,
        onInvoice: paymentMethod,
      });
    },

    handleReset() {
      this.onReset();
    },

    /**
     * @param {String} companyId
     * @param {String} subroute
     */
    handleEdit(companyId, subroute) {
      this.onEdit(companyId, subroute);
    },

    handlePreviousPage() {
      this.onPreviousPage();
    },

    handleNextPage() {
      this.onNextPage();
    },

    /**
     * @param {'user'|'company'} type
     * @param {User|Company} model
     */
    handleProfileLinkClick(type, model) {
      this.onProfileLinkClick(type, model);
    },
  },

  loadCompanyGroups: task(function *() {
    try {
      const response = yield this.companyGroupsCommandService.loadAllCompanyGroups();
      this.shData.pushResponseToStore(response);

      const companyGroups = this.shData.getModelArrayFromResponse(response, 'companyGroup');
      this.set('_availableCompanyGroups', companyGroups);
    } catch (e) {
      this.set('_availableCompanyGroups', []);
    }
  }),

  /**
   * @inheritDoc
   */
   init() {
    this._super(...arguments);
    this.set('_availableCompanyGroups', []);

    this.loadCompanyGroups.perform();
  },
});
